import type { DsrPublished } from '@getaccept/dsr-shared/src/types/dsr';
import { getImageURLQuery, viewerRecipientInputQuery } from '@getaccept/editor-lib';
import { entityQuery } from '@getaccept/lib-shared/src/entity/signing-site/entity.queries';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Entity } from '@getaccept/lib-shared/src/types/entity';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { apolloClient } from '@getaccept/signing-site/src/apollo';
import { DSRMutations } from '../graphql/dsr.mutations';
import { DSRQueries } from '../graphql/dsr.queries';

export class DsrService {
  static async load(): Promise<DsrPublished> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DSRQueries.load,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async loadRevisions(): Promise<DocumentRecipientRevision[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DSRQueries.loadRevisions,
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return document?.documentRevisions || [];
  }

  static async addRecipientView(revision: number): Promise<boolean> {
    const {
      data: { addRecipientView },
    } = await apolloClient.mutate({
      mutation: DSRMutations.addView,
      variables: { revision },
    });
    return addRecipientView;
  }

  static async loadRecipients(): Promise<Recipient[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DSRQueries.loadRecipients,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document?.recipients || [];
  }

  static async loadUser(): Promise<User> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DSRQueries.loadUser,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document?.user;
  }

  static async loadEntity(): Promise<Entity> {
    const {
      data: { entity },
    } = await apolloClient.query({
      query: entityQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return entity;
  }

  static async loadRecipientInput(): Promise<EditorRecipientInput[]> {
    const {
      data: { editorRecipientInput },
    } = await apolloClient.query({
      query: viewerRecipientInputQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return editorRecipientInput;
  }

  static async loadTheme(documentId: string): Promise<SigningTheme> {
    const {
      data: { theme },
    } = await apolloClient.query({
      query: DSRQueries.theme,
      variables: {
        documentId,
      },
    });
    return theme;
  }

  static async getEditorImageURL(
    id: string,
    height?: string
  ): Promise<{ url: string; externalId: string }> {
    const {
      data: { image },
    } = await apolloClient.query({
      query: getImageURLQuery,
      variables: { id, height },
    });

    return { url: image?.url || '', externalId: image?.externalId || undefined };
  }
}
